import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigurationService } from '../../services/configuration.service';
import { HelpDocsService, HelpArticle } from '../../services/help-docs.service';
import { AIAssistantService } from '../../services/ai-assistant.service';
import type { AIAssistantMode, AILeadsContext } from '../../services/ai-assistant.service';

@Component({
  selector: 'app-ai-assistant-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-assistant-modal.component.html',
  styleUrls: ['./ai-assistant-modal.component.css']
})
export class AIAssistantModalComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;
  isVisible = false;
  currentMode: AIAssistantMode = 'help';
  supportChatHistory: { role: 'user' | 'assistant'; content: string; articleIds?: string[]; relatedQuestions?: string[]; relatedLinks?: string[] }[] = [];
  supportChatInput = '';
  isHelpBotLoading = false;
  helpBotError: string | null = null;
  remainingWaitSeconds: number = 0; // Countdown timer for rate limit waits
  private lastHelpBotRequestTime = 0;
  private readonly MIN_REQUEST_INTERVAL = 2000; // 2 seconds minimum between requests
  private destroy$ = new Subject<void>();
  private shouldScroll = false;
  private queuedMessage: string | null = null;
  private retryTimeout: any = null;
  private countdownInterval: any = null;

  suggestedQuestionsHelp: string[] = [
    'How does the app work?',
    'How do I customize my InnovNet Profile?',
    'How do I share my card?',
    'How do I view my contacts?',
    'How do I view my insights?'
  ];
  suggestedQuestionsLeads: string[] = [
    'Who are my hottest leads?',
    'When should I contact my warm leads?',
    'Which contacts should I follow up with first?',
    'Give me networking tips for my leads'
  ];
  /** When opened from a contact detail page, suggestions are about that contact only. */
  leadsContext: AILeadsContext | null = null;

  get suggestedQuestions(): string[] {
    if (this.currentMode !== 'leads') return this.suggestedQuestionsHelp;
    if (this.leadsContext?.contactName) {
      const name = this.leadsContext.contactName;
      return [
        `When should I contact ${name}?`,
        `What's the best way to follow up with ${name}?`,
        `Summarize ${name}'s engagement and interest`,
        `Give me talking points for reaching out to ${name}`
      ];
    }
    return this.suggestedQuestionsLeads;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigurationService,
    private helpDocs: HelpDocsService,
    private aiAssistantService: AIAssistantService
  ) {}

  ngOnInit(): void {
    this.aiAssistantService.isVisible$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(visible => {
      this.isVisible = visible;
      if (visible) {
        setTimeout(() => {
          const input = document.querySelector('.ai-assistant-input') as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      }
    });
    this.aiAssistantService.mode$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((mode: AIAssistantMode) => {
      this.currentMode = mode;
      if (mode === 'leads') {
        this.clearSupportChat();
      }
    });
    this.aiAssistantService.leadsContext$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(ctx => {
      this.leadsContext = ctx;
    });
    this.aiAssistantService.pendingMessage$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(message => {
      if (!message || !this.isVisible || this.isHelpBotLoading) return;
      this.supportChatInput = message;
      this.aiAssistantService.consumePendingMessage();
      this.sendHelpBotMessage();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clear retry timeout and countdown on destroy
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  scrollToBottom(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(_event: Event): void {
    if (this.isVisible) {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.aiAssistantService.close();
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  private getLeadsChatBody(message: string): { message: string; conversationHistory: { role: string; content: string }[]; contactId?: number; contactName?: string; contactSnapshot?: string } {
    const body: { message: string; conversationHistory: { role: string; content: string }[]; contactId?: number; contactName?: string; contactSnapshot?: string } = {
      message,
      conversationHistory: this.supportChatHistory.map(h => ({ role: h.role, content: h.content })),
    };
    if (this.leadsContext) {
      body.contactId = this.leadsContext.contactId;
      body.contactName = this.leadsContext.contactName;
      if (this.leadsContext.contactSnapshot) {
        body.contactSnapshot = this.leadsContext.contactSnapshot;
      }
    }
    return body;
  }

  sendHelpBotMessage(): void {
    const msg = (this.supportChatInput || '').trim();
    if (!msg || this.isHelpBotLoading) return;
    
    // Rate limiting: enforce minimum time between requests
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastHelpBotRequestTime;
    if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
      const waitTime = Math.ceil((this.MIN_REQUEST_INTERVAL - timeSinceLastRequest) / 1000);
      this.helpBotError = `Please wait ${waitTime} second${waitTime !== 1 ? 's' : ''} before asking another question.`;
      return;
    }
    
    this.supportChatInput = '';
    this.helpBotError = null;
    this.isHelpBotLoading = true;
    this.lastHelpBotRequestTime = now;
    // Scroll to bottom when loading starts
    setTimeout(() => this.shouldScroll = true, 100);

    const apiUrl = this.configService.getApiBaseUrl();
    const body = this.currentMode === 'leads' ? this.getLeadsChatBody(msg) : {
      message: msg,
      conversationHistory: this.supportChatHistory.map(h => ({ role: h.role, content: h.content })),
    };

    if (this.currentMode === 'leads') {
      this.http.post<{ success: boolean; reply?: string; error?: string; rateLimited?: boolean; waitSeconds?: number }>(
        `${apiUrl}/leads_ai_chat.php`,
        body,
        { withCredentials: true }
      ).subscribe({
        next: (res) => {
          this.isHelpBotLoading = false;
          this.queuedMessage = null;
          if (res.success && res.reply != null) {
            this.supportChatHistory.push({ role: 'user', content: msg });
            this.supportChatHistory.push({ role: 'assistant', content: res.reply });
            this.shouldScroll = true;
          } else if (res.rateLimited && res.waitSeconds) {
            this.handleRateLimit(msg, res.waitSeconds);
          } else {
            this.helpBotError = res.error ?? 'Something went wrong. Please try again.';
          }
        },
        error: (err) => {
          this.isHelpBotLoading = false;
          if (err.error && typeof err.error === 'object' && err.error.rateLimited) {
            this.handleRateLimit(msg, err.error.waitSeconds || 5);
          } else {
            this.helpBotError = 'Unable to reach AI. Please try again.';
          }
        },
      });
      return;
    }

    this.http.post<{ success: boolean; reply?: string; articleIds?: string[]; relatedQuestions?: string[]; error?: string; rateLimited?: boolean; waitSeconds?: number }>(
      `${apiUrl}/help_bot.php`,
      body,
      { withCredentials: true }
    ).subscribe({
      next: (res) => {
        this.isHelpBotLoading = false;
        this.queuedMessage = null; // Clear queued message on success
        
        if (res.success && res.reply != null) {
          // Separate links from questions
          const questions: string[] = [];
          const links: string[] = [];
          
          if (res.relatedQuestions && res.relatedQuestions.length > 0) {
            res.relatedQuestions.forEach(item => {
              if (this.isRelatedQuestionLink(item)) {
                links.push(item);
              } else {
                questions.push(item);
              }
            });
          }
          
          // Deduplicate article IDs by destination (route)
          const uniqueArticleIds: string[] = [];
          const seenRoutes = new Set<string>();
          
          if (res.articleIds && res.articleIds.length > 0) {
            res.articleIds.forEach(articleId => {
              const route = this.getArticleRoute(articleId);
              if (!seenRoutes.has(route)) {
                seenRoutes.add(route);
                uniqueArticleIds.push(articleId);
              }
            });
          }
          
          this.supportChatHistory.push({ role: 'user', content: msg });
          this.supportChatHistory.push({ 
            role: 'assistant', 
            content: res.reply,
            articleIds: uniqueArticleIds.length > 0 ? uniqueArticleIds : undefined,
            relatedQuestions: questions.length > 0 ? questions : undefined,
            relatedLinks: links.length > 0 ? links : undefined
          });
          // Scroll to bottom after new messages
          this.shouldScroll = true;
        } else if (res.rateLimited && res.waitSeconds) {
          // Rate limited - queue the message and auto-retry
          this.handleRateLimit(msg, res.waitSeconds);
        } else {
          this.helpBotError = res.error ?? 'Something went wrong. Please try again.';
        }
      },
      error: (err) => {
        this.isHelpBotLoading = false;
        // Check if it's a rate limit error from the backend
        if (err.error && typeof err.error === 'object' && err.error.rateLimited) {
          const waitSeconds = err.error.waitSeconds || 5;
          this.handleRateLimit(msg, waitSeconds);
        } else {
          this.helpBotError = 'Unable to reach help service. Please try again.';
        }
      },
    });
  }

  clearSupportChat(): void {
    this.supportChatHistory = [];
    this.supportChatInput = '';
    this.helpBotError = null;
    this.queuedMessage = null;
    this.remainingWaitSeconds = 0;
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private handleRateLimit(message: string, waitSeconds: number): void {
    // Store the message to retry
    this.queuedMessage = message;
    
    // Add user message to chat history immediately (so they see their question)
    this.supportChatHistory.push({ role: 'user', content: message });
    this.shouldScroll = true;
    
    // Keep loading state active
    this.isHelpBotLoading = true;
    this.helpBotError = null; // Don't show error message
    
    // Clear any existing retry timeout and countdown
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    // Start countdown timer
    this.remainingWaitSeconds = waitSeconds;
    this.countdownInterval = setInterval(() => {
      this.remainingWaitSeconds--;
      if (this.remainingWaitSeconds <= 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        this.remainingWaitSeconds = 0;
      }
    }, 1000);
    
    // Auto-retry after wait time (convert seconds to milliseconds)
    const waitMs = waitSeconds * 1000;
    this.retryTimeout = setTimeout(() => {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.remainingWaitSeconds = 0;
      if (this.queuedMessage) {
        const msgToRetry = this.queuedMessage;
        this.queuedMessage = null;
        // Retry the request
        this.retryHelpBotMessage(msgToRetry);
      }
    }, waitMs);
  }

  private retryHelpBotMessage(msg: string): void {
    this.lastHelpBotRequestTime = Date.now();
    const apiUrl = this.configService.getApiBaseUrl();
    const body = this.currentMode === 'leads' ? this.getLeadsChatBody(msg) : {
      message: msg,
      conversationHistory: this.supportChatHistory.map(h => ({ role: h.role, content: h.content })),
    };

    if (this.currentMode === 'leads') {
      this.http.post<{ success: boolean; reply?: string; error?: string; rateLimited?: boolean; waitSeconds?: number }>(
        `${apiUrl}/leads_ai_chat.php`,
        body,
        { withCredentials: true }
      ).subscribe({
        next: (res) => {
          this.isHelpBotLoading = false;
          this.queuedMessage = null;
          this.remainingWaitSeconds = 0;
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
          }
          if (res.success && res.reply != null) {
            const userMsgIndex = this.supportChatHistory.findIndex(m => m.role === 'user' && m.content === msg);
            if (userMsgIndex >= 0 && userMsgIndex + 1 < this.supportChatHistory.length && this.supportChatHistory[userMsgIndex + 1].role === 'assistant') {
              this.supportChatHistory.splice(userMsgIndex + 1, 1);
            }
            const insertIndex = this.supportChatHistory.findIndex(m => m.role === 'user' && m.content === msg) + 1;
            if (insertIndex > 0) {
              this.supportChatHistory.splice(insertIndex, 0, { role: 'assistant', content: res.reply });
            } else {
              this.supportChatHistory.push({ role: 'assistant', content: res.reply });
            }
            this.shouldScroll = true;
          } else if (res.rateLimited && res.waitSeconds) {
            this.handleRateLimit(msg, res.waitSeconds);
          } else {
            this.helpBotError = res.error ?? 'Something went wrong.';
          }
        },
        error: (err) => {
          this.isHelpBotLoading = false;
          if (err.error && typeof err.error === 'object' && err.error.rateLimited) {
            this.handleRateLimit(msg, err.error.waitSeconds || 5);
          } else {
            this.helpBotError = 'Unable to reach AI. Please try again.';
          }
        },
      });
      return;
    }

    this.http.post<{ success: boolean; reply?: string; articleIds?: string[]; relatedQuestions?: string[]; error?: string; rateLimited?: boolean; waitSeconds?: number }>(
      `${apiUrl}/help_bot.php`,
      body,
      { withCredentials: true }
    ).subscribe({
      next: (res) => {
        this.isHelpBotLoading = false;
        this.queuedMessage = null;
        this.remainingWaitSeconds = 0;
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        
        if (res.success && res.reply != null) {
          // Separate links from questions
          const questions: string[] = [];
          const links: string[] = [];
          
          if (res.relatedQuestions && res.relatedQuestions.length > 0) {
            res.relatedQuestions.forEach(item => {
              if (this.isRelatedQuestionLink(item)) {
                links.push(item);
              } else {
                questions.push(item);
              }
            });
          }
          
          // Deduplicate article IDs by destination (route)
          const uniqueArticleIds: string[] = [];
          const seenRoutes = new Set<string>();
          
          if (res.articleIds && res.articleIds.length > 0) {
            res.articleIds.forEach(articleId => {
              const route = this.getArticleRoute(articleId);
              if (!seenRoutes.has(route)) {
                seenRoutes.add(route);
                uniqueArticleIds.push(articleId);
              }
            });
          }
          
          // Find the user message in history and add assistant response after it
          const userMsgIndex = this.supportChatHistory.findIndex(m => m.role === 'user' && m.content === msg);
          if (userMsgIndex >= 0) {
            // Remove any existing assistant response after this user message (in case of retry)
            if (userMsgIndex + 1 < this.supportChatHistory.length && this.supportChatHistory[userMsgIndex + 1].role === 'assistant') {
              this.supportChatHistory.splice(userMsgIndex + 1, 1);
            }
            // Insert assistant response after user message
            this.supportChatHistory.splice(userMsgIndex + 1, 0, {
              role: 'assistant',
              content: res.reply,
              articleIds: uniqueArticleIds.length > 0 ? uniqueArticleIds : undefined,
              relatedQuestions: questions.length > 0 ? questions : undefined,
              relatedLinks: links.length > 0 ? links : undefined
            });
          } else {
            // Fallback: add to end if user message not found
            this.supportChatHistory.push({
              role: 'assistant',
              content: res.reply,
              articleIds: uniqueArticleIds.length > 0 ? uniqueArticleIds : undefined,
              relatedQuestions: questions.length > 0 ? questions : undefined,
              relatedLinks: links.length > 0 ? links : undefined
            });
          }
          // Scroll to bottom after new messages
          this.shouldScroll = true;
        } else if (res.rateLimited && res.waitSeconds) {
          // Still rate limited - retry again
          this.handleRateLimit(msg, res.waitSeconds);
        } else {
          this.helpBotError = res.error ?? 'Something went wrong. Please try again.';
        }
      },
      error: (err) => {
        // Check if it's still a rate limit error
        if (err.error && typeof err.error === 'object' && err.error.rateLimited) {
          const waitSeconds = err.error.waitSeconds || 5;
          this.handleRateLimit(msg, waitSeconds);
        } else {
          this.isHelpBotLoading = false;
          this.helpBotError = 'Unable to reach help service. Please try again.';
        }
      },
    });
  }

  getArticleTitle(articleId: string): string {
    const route = this.getArticleRoute(articleId);
    
    if (route === '/customTheme' || route.startsWith('/customTheme')) {
      return route.includes('tab=share')
        ? 'My Profile > Share Innovnet profile'
        : 'InnovNet Profile';
    } else if (route === '/insights') {
      return 'Insights';
    } else if (route === '/exchangedContacts') {
      return 'Contacts';
    } else if (route.startsWith('/settings#')) {
      const sectionId = route.replace('/settings#', '');
      const sectionNames: Record<string, string> = {
        'card-information': 'InnovNet Profile',
        'digital-wallet': 'Digital Wallet',
        'contact-form-config': 'Contact Form Configuration',
        'remembered-devices': 'Remembered Devices',
        'billing': 'Billing',
        'support': 'Support',
        'notifications': 'Notifications',
        'email-templates': 'Automated Email Templates',
        'qr-payment': 'QR Payment',
        'gmail-integration': 'Gmail Integration',
        'hubspot-integration': 'HubSpot Integration',
      };
      const sectionName = sectionNames[sectionId] || sectionId;
      return 'Settings > ' + sectionName;
    } else if (route.startsWith('/settings')) {
      return 'Settings';
    }
    
    return articleId;
  }

  getArticleRoute(articleId: string): string {
    const routeMap: Record<string, string> = {
      'getting-started': '/customTheme',
      'add-data-template': '/customTheme',
      'field-meanings': '/customTheme',
      'validation-rules': '/customTheme',
      'notifications': '/settings#notifications',
      'view-insights': '/insights',
      'insight-types': '/insights',
      'not-saving': '/customTheme',
      'change-colors': '/customTheme',
      'navigation': '/settings#support',
      'error-messages': '/settings#support',
      'account-settings': '/settings',
      'saving-data': '/customTheme',
      'contact-form-config': '/customTheme?tab=exchange-form',
      'share-card': '/customTheme?tab=share',
      'view-contacts': '/exchangedContacts',
      'digital-wallet': '/customTheme?tab=share',
      'qr-code': '/customTheme?tab=share',
      'card-link': '/customTheme?tab=share',
      'export-contacts': '/exchangedContacts',
      'subscription-billing': '/settings#billing',
      'change-password': '/settings',
      'reset-password': '/settings',
      'photo-upload-issues': '/customTheme',
      'add-videos': '/customTheme',
      'about-me-tips': '/customTheme',
      'social-media-links': '/customTheme',
      'custom-links': '/customTheme',
      'photo-grid': '/customTheme',
      'theme-selection': '/customTheme',
      'color-customization': '/customTheme',
      'section-ordering': '/customTheme',
      'weekly-reports': '/settings#notifications',
      'remembered-devices': '/settings#remembered-devices',
      'integrations-gmail': '/settings#gmail-integration',
      'integrations-hubspot': '/settings#hubspot-integration',
      'premium-features': '/settings#billing',
      'contact-exchange-process': '/customTheme?tab=exchange-form',
      'privacy-visibility': '/settings',
      'troubleshooting-general': '/settings#support',
      'browser-compatibility': '/settings#support',
      'settings-tabs-overview': '/settings#support',
      'settings-share-innovnet-profile': '/customTheme?tab=share',
      'settings-innovnet-card-tab': '/settings#card-information',
      'settings-email-templates': '/settings#email-templates',
      'settings-qr-payment': '/settings#qr-payment',
    };
    return routeMap[articleId] || '/settings#support';
  }

  openArticleInNewTab(articleId: string): void {
    const route = this.getArticleRoute(articleId);
    window.open(route, '_blank');
  }

  isRelatedQuestionLink(question: string): boolean {
    return /^https?:\/\//i.test((question || '').trim());
  }

  isRelatedQuestion(question: string): boolean {
    const t = (question || '').trim();
    return /^(How|What|Where|Why|Can|When|Which|Who|Do|Does|Is|Are|Will|Would|Should|Could|May|Might)/i.test(t) || t.endsWith('?');
  }

  handleRelatedQuestionClick(question: string): void {
    if (this.isRelatedQuestion(question)) {
      this.supportChatInput = question.trim().replace(/\?$/, '');
      this.sendHelpBotMessage();
    } else {
      this.supportChatInput = question.trim();
      this.sendHelpBotMessage();
    }
  }

  handleRelatedLinkClick(link: string): void {
    window.open(link.trim(), '_blank');
  }

  getLinkActionText(link: string): string {
    try {
      const url = new URL(link);
      const path = url.pathname.toLowerCase();
      
      if (path.includes('design') || path.includes('profile')) {
        return 'Learn more about designing your profile';
      } else if (path.includes('settings')) {
        return 'View settings guide';
      } else if (path.includes('help')) {
        return 'Read help article';
      } else if (path.includes('guide')) {
        return 'Read guide';
      } else {
        return 'Learn more';
      }
    } catch {
      return 'Learn more';
    }
  }

  handleSuggestedQuestionClick(question: string): void {
    this.supportChatInput = question;
    this.sendHelpBotMessage();
  }
}
